/**
 * The design tokens, and how an operator rebrands them.
 *
 * Every colour, radius and typeface the stylesheet uses is a CSS custom
 * property with a default from `design_mockup.png`. Nothing in `styles.ts`
 * writes a literal colour, which is what makes "any casino can put their own
 * brand on this" true rather than aspirational — an operator sets
 * `accent-color="#0a7"` on the element and the whole panel follows, including
 * the states derived from it.
 *
 * **Operator values never reach the stylesheet as text.** They are applied with
 * `style.setProperty()` on the host, so a value is stored as a value and cannot
 * close a rule and open another. This is the same argument as `textContent` in
 * `element.ts`: the injection is not blocked by a check someone has to remember,
 * there is nowhere for it to go. `sanitiseThemeValue` is the second line —
 * a conservative shape check so a value that is syntactically legal but
 * obviously not a colour (`url(https://…)`, which would fetch) is refused and
 * the default kept.
 */

/** What kind of value an attribute carries, and therefore what shape is legal. */
export type ThemeValueKind = 'color' | 'length' | 'font';

export interface ThemeToken {
  /** The attribute an operator writes on `<betting-widget>`. */
  attribute: string;
  /** The custom property it sets. */
  property: string;
  kind: ThemeValueKind;
  /** The mockup's value, used when the attribute is absent or refused. */
  fallback: string;
}

/**
 * The brand surface: what an operator can change without touching our CSS.
 *
 * Deliberately short. Every token here is one an operator plausibly needs and
 * one the rest of the sheet derives from — exposing forty knobs would mean
 * forty ways to produce a panel that no longer reads as glass.
 */
export const THEME_TOKENS: readonly ThemeToken[] = [
  // Sampled from design_mockup.png: the player bubble, the featured pill and
  // the confirm button are all this purple.
  { attribute: 'accent-color', property: '--geni-accent', kind: 'color', fallback: '#5223d3' },
  // The logo mark, and the far end of every accent gradient.
  { attribute: 'accent-deep-color', property: '--geni-accent-deep', kind: 'color', fallback: '#471faa' },
  // What the glass is tinted with before anything is blurred behind it.
  { attribute: 'surface-color', property: '--geni-surface', kind: 'color', fallback: '#ffffff' },
  { attribute: 'text-color', property: '--geni-text', kind: 'color', fallback: '#16141f' },
  { attribute: 'radius', property: '--geni-radius', kind: 'length', fallback: '26px' },
  {
    attribute: 'font-family',
    property: '--geni-font',
    kind: 'font',
    // The system stack, which is what makes it look native on every platform.
    fallback:
      '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif',
  },
] as const;

/**
 * Shapes we will accept, per kind.
 *
 * An allowlist rather than a denylist. The set of dangerous CSS values is not
 * one anybody can enumerate; the set of things a colour looks like is.
 */
const SHAPES: Record<ThemeValueKind, RegExp> = {
  // #rgb, #rrggbb(aa), rgb()/rgba()/hsl()/hsla()/oklch()/color-mix(), or a bare
  // keyword like `rebeccapurple`. Function arguments are restricted to numbers,
  // percentages, separators and nested colour keywords — never `url(`.
  color:
    /^(#[0-9a-f]{3,8}|[a-z]+|(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch|color-mix)\([a-z0-9%.,\s/+-]*\))$/i,
  // A single length or percentage. No `calc(`, which can carry anything.
  length: /^-?\d*\.?\d+(px|rem|em|%|vh|vw|pt)$/i,
  // Family names and the generic keywords, comma separated. Quotes allowed
  // because family names contain spaces; nothing else that could open a token.
  font: /^["'a-z0-9\s,.-]+$/i,
};

/**
 * A value we are willing to hand to `setProperty`, or null to keep the default.
 *
 * Returns null rather than throwing: a mistyped brand colour should leave the
 * widget looking like the mockup, not take the panel down on a live site.
 */
export function sanitiseThemeValue(kind: ThemeValueKind, raw: string | null): string | null {
  if (raw === null) return null;

  const value = raw.trim();
  if (value === '') return null;

  // Length caps the damage from a pathological value before the regex runs.
  if (value.length > 200) return null;

  // Belt and braces around the shapes: these three can never appear in a value
  // we accept, and each is a way to fetch or to escape.
  if (/url\(|expression|@import|[;{}<>\\]/i.test(value)) return null;

  return SHAPES[kind].test(value) ? value : null;
}

/**
 * The custom properties to set on the host, given a way to read attributes.
 *
 * Takes a reader rather than an element so it is a pure function over strings —
 * the whole mapping is testable without a DOM, on the same terms as the rest of
 * the widget.
 *
 * **Only what the operator actually supplied.** Writing `token.fallback` for
 * the rest looks harmless and even tidy — the host would carry the complete
 * resolved theme — and it silently breaks dark mode. An inline custom property
 * outranks every rule in the stylesheet, including `:host([theme="dark"])` and
 * the `prefers-color-scheme` block, so writing the light default for
 * `--geni-surface` and `--geni-text` pins the widget to light glass with
 * dark text for ever. It fails in the least visible way possible: the tokens
 * that are *not* written inline — the bevels, the shadow, the alphas — do
 * switch, so a dark panel is almost right and reads as a styling opinion rather
 * than as a bug.
 *
 * The defaults live in the stylesheet's own `:host` block, which is where a
 * default belongs: it is the weakest thing in the cascade, so every later rule
 * can still win. `themeDefaultsDeclared` keeps the two lists honest about each
 * other.
 */
export function themeProperties(
  readAttribute: (name: string) => string | null,
): Map<string, string> {
  const properties = new Map<string, string>();

  for (const token of THEME_TOKENS) {
    const supplied = sanitiseThemeValue(token.kind, readAttribute(token.attribute));
    if (supplied !== null) properties.set(token.property, supplied);
  }

  return properties;
}

/**
 * Every token whose documented fallback is not the one the stylesheet declares.
 *
 * The two have to agree: `fallback` is what this module tells the world the
 * default is, and the `:host` block is what actually renders. Nothing enforces
 * that at runtime, so a test asks this instead.
 */
export function themeDefaultsDeclared(stylesheet: string): ThemeToken[] {
  const host = /:host\s*\{([^}]*)\}/.exec(stylesheet)?.[1] ?? '';

  return THEME_TOKENS.filter((token) => {
    const declared = new RegExp(`${token.property}\\s*:\\s*([^;]+);`).exec(host)?.[1]?.trim();
    return declared !== token.fallback.trim();
  });
}

/** `auto` follows the host page; the other two are an operator overriding it. */
export type ThemePreference = 'auto' | 'light' | 'dark';

export function themePreference(raw: string | null): ThemePreference {
  return raw === 'light' || raw === 'dark' ? raw : 'auto';
}
